import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Edit3 } from 'lucide-react'
import { clientService, type Client } from '../services/clientService'
import { tagService, type ContactTag } from '../services/tagService'
import { ClientEditModal } from '../components/common/ClientEditModal'
import { TagFilterPanel } from '../components/common/TagFilterPanel'
import toast from 'react-hot-toast'

const SEARCH_DEBOUNCE_MS = 400
const DEFAULT_PAGE_SIZE = 20

const Clients: React.FC = () => {
  const navigate = useNavigate()
  const [clients, setClients] = useState<Client[]>([])
  const [pagination, setPagination] = useState({ page: 1, limit: DEFAULT_PAGE_SIZE, total: 0, totalPages: 0 })
  const [isLoading, setIsLoading] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [searchDebounced, setSearchDebounced] = useState('')
  const [editingClient, setEditingClient] = useState<Client | null>(null)
  const [availableTags, setAvailableTags] = useState<ContactTag[]>([])
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([])
  const [tagMatch, setTagMatch] = useState<'any' | 'all'>('any')

  const loadTagsCatalog = useCallback(async () => {
    try {
      const res = await tagService.listTags()
      if (res.success && Array.isArray(res.data)) {
        setAvailableTags(res.data)
      }
    } catch {
      /* silencioso: filtros de tag são opcionais */
    }
  }, [])

  const loadClients = useCallback(
    async (page: number = 1) => {
      try {
        setIsLoading(true)
        const res = await clientService.listClients({
          page,
          limit: DEFAULT_PAGE_SIZE,
          search: searchDebounced || undefined,
          include_tags: true,
          tag_ids:
            selectedTagIds.length > 0
              ? [...selectedTagIds].sort((a, b) => a - b).join(',')
              : undefined,
          tag_match: selectedTagIds.length > 0 ? tagMatch : undefined
        })
        if (res.success && res.data) {
          setClients(res.data.clients)
          setPagination(res.data.pagination)
        } else {
          setClients([])
        }
      } catch (error: unknown) {
        const err = error as { response?: { status?: number; data?: { code?: string } } }
        if (err.response?.status === 400 && err.response?.data?.code === 'INVALID_TAG_IDS') {
          toast.error('Uma ou mais tags selecionadas não estão disponíveis para o seu usuário.')
        } else {
          toast.error('Erro ao carregar clientes')
        }
        setClients([])
      } finally {
        setIsLoading(false)
      }
    },
    [searchDebounced, selectedTagIds, tagMatch]
  )

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(searchInput.trim()), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [searchInput])

  useEffect(() => {
    loadTagsCatalog()
  }, [loadTagsCatalog])

  useEffect(() => {
    loadClients(1)
  }, [loadClients])

  const goToPage = (page: number) => {
    if (page < 1 || page > pagination.totalPages) return
    setEditingClient(null)
    loadClients(page)
  }

  const handleClientSaved = (updated: Client) => {
    setClients(prev => prev.map(c => (c.id === updated.id ? { ...c, ...updated } : c)))
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-4 mb-6">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="p-2 rounded-lg hover:bg-gray-200 transition-colors"
            aria-label="Voltar ao dashboard"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Clientes
            </h1>
            <p className="text-gray-600">Gerencie os dados basicos do cliente.</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-4 mb-4 space-y-4">
          <div>
            <label htmlFor="client-search" className="block text-sm font-medium text-gray-700 mb-2">
              Buscar
            </label>
            <input
              id="client-search"
              type="text"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder="Nome, telefone, e-mail ou documento"
              className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            />
          </div>

          <div className="max-w-2xl">
            <TagFilterPanel
              availableTags={availableTags}
              selectedTagIds={selectedTagIds}
              onChangeSelectedTagIds={setSelectedTagIds}
              tagMatch={tagMatch}
              onChangeTagMatch={setTagMatch}
              usageItems={clients}
              radioGroupName="tagMatchClients"
              clearButtonClassName="text-amber-700 hover:underline"
            />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">Carregando clientes...</div>
          ) : clients.length === 0 ? (
            <div className="p-8 text-center text-gray-500">Nenhum cliente encontrado.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">E-mail</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Documento</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Telefone</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tags</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pular menu do bot</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {clients.map(client => (
                    <tr key={client.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">{client.name ?? '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{client.email ?? '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{client.document ?? '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{client.phone_number}</td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex flex-wrap gap-1 max-w-[200px]">
                          {client.tags && client.tags.length > 0 ? (
                            client.tags.map(t => (
                              <span
                                key={t.id}
                                className="text-[10px] px-1.5 py-0.5 rounded border border-gray-200 bg-white truncate max-w-[100px]"
                                style={{ borderColor: t.color }}
                                title={t.name}
                              >
                                {t.name}
                              </span>
                            ))
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={client.skip_bot_menu ? 'text-amber-600 font-medium' : 'text-gray-500'}>
                          {client.skip_bot_menu ? 'Sim' : 'Não'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => setEditingClient(client)}
                          className="p-2 rounded-lg hover:bg-amber-100 text-amber-600"
                          title="Editar"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {pagination.totalPages > 1 && (
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
              <span className="text-sm text-gray-600">
                Página {pagination.page} de {pagination.totalPages} ({pagination.total} clientes)
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => goToPage(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                <button
                  type="button"
                  onClick={() => goToPage(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Próxima
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <ClientEditModal
        isOpen={!!editingClient}
        onClose={() => setEditingClient(null)}
        client={editingClient}
        onSaved={handleClientSaved}
      />
    </div>
  )
}

export default Clients
